import type { Gender, IsoDate } from '../../../../core/primitives/schema';

/**
 * The signed-in employee's own record — owned by this feature, implemented in
 * `infrastructure/` (blueprint §3.4, §4.1).
 *
 * Every method here concerns *the caller's own* record. There is deliberately
 * no "fetch employee by id": the security rules refuse it anyway, and a port
 * that offers the shape invites a screen to try.
 */

/** What the import supplies, plus what the employee fills in. */
export interface MyEmployeeRecord {
  readonly id: string;
  readonly employeeNumber: string;
  readonly fullName: string;
  readonly branchId: string | null;
  readonly phone: string | null;
  readonly address: string | null;
  readonly workEmail: string | null;

  /** Blank after import; the employee completes these. */
  readonly nationalId: string | null;
  readonly birthDate: IsoDate | null;
  readonly gender: Gender | null;

  /**
   * Cross-check values from the roster, not facts. They say what to expect —
   * the dependants the employee actually enters are what count.
   */
  readonly declaredMaritalStatus: boolean | null;
  readonly declaredChildrenCount: number;

  readonly enrolledInFund: boolean;
}

/*
 * There is deliberately no `profileComplete` flag.
 *
 * Completeness is a property of the fields above, so a stored flag can only
 * ever agree with them or lie about them — and the security rules do not let an
 * employee write such a flag anyway, so it could never become true by the one
 * route that matters. `outstandingProfileFields` derives it instead, and cannot
 * disagree with the data it reads.
 */

/** Only the fields an employee may write. The rules enforce the same list. */
export interface MyProfileUpdate {
  readonly nationalId: string;
  readonly birthDate: IsoDate;
  readonly gender: Gender;
  readonly phone: string;
  readonly address: string;
}

export type EmployeeFailureCode = 'not_found' | 'forbidden' | 'network' | 'unknown';

export class EmployeeError extends Error {
  readonly code: EmployeeFailureCode;

  constructor(code: EmployeeFailureCode, message: string) {
    super(message);
    this.name = 'EmployeeError';
    this.code = code;
  }
}

export interface EmployeePort {
  /** The caller's own record, resolved through their account link. */
  getMyRecord(): Promise<MyEmployeeRecord>;

  /**
   * Saves the fields the employee owns.
   *
   * Coverage, enrolment, employment status, branch, and employee number are
   * absent by construction — a client cannot send what the type has no place
   * for, and the rules refuse them regardless (spec FR-011).
   */
  updateMyProfile(update: MyProfileUpdate): Promise<void>;
}
